import {TFunction} from 'i18next';
import {filter, find, includes, isEmpty, map, omitBy, pickBy, transform, some, isNil} from 'lodash';

import {AccountAssignmentInfo, CustomField, CustomFieldType} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, AssignmentData} from 'types';
import {apiUtils, generateUtils} from 'utils';

import {buildTranslationKey} from './transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.LIVE_HELP;
const T_KEYS_LABELS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;

const FIELD_VALIDATION_MESSAGES = {
    id: T_KEYS.ERRORS.USER_ID,
    firstName: T_KEYS.ERRORS.USER_FIRST_NAME,
    lastName: T_KEYS.ERRORS.USER_LAST_NAME
};

const getUserInfoFields = (fields: CustomField[] = []): CustomField[] =>
    filter(fields, (field) => field.type !== CustomFieldType.Object);

const getControlOnlyFields = (fields: CustomField[] = []): CustomField[] => {
    const controlOnlyInfoField = find(fields, (f) => f.name === 'controlOnlyInfo');
    return controlOnlyInfoField?.children || [];
};

const getUserInfoFieldLabel = (field: CustomField, t: TFunction) => {
    if (field.title) {
        return field.title;
    }

    const key = buildTranslationKey(field.name);
    const translationKey = T_KEYS_LABELS[key];
    return translationKey ? t(translationKey) : field.name;
};

const getAssignmentUserInfoFields = (
    userInfoFieldsData: AssignmentData<CustomField[]>[], assignmentInfo: AccountAssignmentInfo
) => {
    const filterQuery = assignmentInfo?.groupId ?
        {assignment: {groupId: assignmentInfo?.groupId}} : {account: true};
    return (find(userInfoFieldsData, filterQuery) as AssignmentData<CustomField[]>)?.data;
};

const getUserInfoFullPath = (fieldName: string, basePath: string) => basePath ? `${basePath}.${fieldName}` : fieldName;

const getUserInfoErrors = (
    _error: ApiError, t: TFunction, basePath = ''
): Record<string, string> => {
    const error = _error?.response?.data;
    const _errors = error?.errors ? Object.keys(error.errors) : [];

    if (!isEmpty(_errors)) {
        const fieldErrors = map(_errors, (field) => {
            const overrides = FIELD_VALIDATION_MESSAGES[field] ?
                {[field]: t(FIELD_VALIDATION_MESSAGES[field])} : {};
            return {
                field,
                overrides
            };
        });

        const allFields = transform(fieldErrors, (acc, current) => {
            const fullFieldName = getUserInfoFullPath(current.field, basePath);
            if (includes(fullFieldName, 'userInfo')) {
                acc.push({field: fullFieldName});
            }
        }, []);
        return apiUtils.getFieldsValidationErrorMessages(_error, t, allFields);
    }

    return {};
};

const generateEmpty = () => ({
    controlOnlyInfo: {},
    id: generateUtils.generateClientId()
});

const isNotEmpty = (value: any) => !isEmpty(value);

const filterNotEmptyProps = (obj: Record<string, any>) => pickBy(obj, isNotEmpty);

// Keep only values that are actually set: drop null/undefined and empty strings, but preserve
// meaningful falsy values like 0 and false. A cleared formatted field (email/phone) would otherwise
// be sent as "" and rejected by the schema's `format` check instead of being removed (LVC-3127).
const isPresent = (value: any) => !isNil(value) && value !== '';

const filterFields = (obj: Record<string, any>, fields: CustomField[]) => {
    if (fields) {
        return pickBy(obj, (value, key) => isPresent(value) && some(fields, (field) => field.name === key));
    }
    return pickBy(obj, isPresent);
};

const format = (
    userInfo: Record<string, any>,
    schema?: {userInfoFields: CustomField[], controlOnlyFields: CustomField[]}
) => {
    const controlOnlyInfo = userInfo.controlOnlyInfo || {};

    const filteredUserInfoFields = filterFields(omitBy(userInfo, 'controlOnlyInfo'), schema?.userInfoFields);
    const filteredControlOnlyInfo = filterFields(controlOnlyInfo, schema?.controlOnlyFields);

    return {
        ...filteredUserInfoFields,
        controlOnlyInfo: filteredControlOnlyInfo
    };
};

export const userInfoUtils = {
    getUserInfoFields,
    format,
    filterNotEmptyProps,
    generateEmpty,
    getAssignmentUserInfoFields,
    getUserInfoFieldLabel,
    getUserInfoFullPath,
    getControlOnlyFields,
    getUserInfoErrors
};